import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

// Public pages
import { WelcomePage } from '@/pages/WelcomePage';
import { LoginPage } from '@/pages/LoginPage';
import { RegisterPage } from '@/pages/RegisterPage';
import { ContactPage } from '@/pages/ContactPage';
import { PricingPage } from '@/pages/PricingPage';
import { OAuthCallbackPage } from '@/pages/OAuthCallbackPage';

// App pages
import PipelinePage from '@/pages/PipelinePage';
import JobDetailPage from '@/pages/JobDetailPage';
import { DashboardPage } from '@/pages/DashboardPage';
import AnalyticsPage from '@/pages/AnalyticsPage';
import DocumentsPage from '@/pages/DocumentsPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { OnboardingPage } from '@/pages/OnboardingPage';
import { ReferralPage } from '@/pages/ReferralPage';

// Layout
import { MainLayout } from '@/components/layout/MainLayout';
import { PrivateRoute } from '@/routes/PrivateRoute';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';

export function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Root redirects to app (PrivateRoute handles auth) */}
        <Route path="/" element={<Navigate to="/app" replace />} />
        <Route path="/contact" element={<ContactPage />} />

        {/* Auth Routes — Google OAuth only */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        {/* Email/password flows redirect to login (Google OAuth replaces them) */}
        <Route path="/forgot-password" element={<Navigate to="/login" replace />} />
        <Route path="/reset-password" element={<Navigate to="/login" replace />} />

        {/* OAuth callback */}
        <Route path="/auth/callback" element={<OAuthCallbackPage />} />

        {/* Welcome page (opened on extension install) */}
        <Route path="/welcome" element={<WelcomePage />} />

        {/* Pricing page */}
        <Route path="/pricing" element={<PricingPage />} />

        {/* Legacy redirects */}
        <Route path="/home" element={<Navigate to="/app" replace />} />

        {/* Deprecated route redirects */}
        <Route path="/app/cv-generator" element={<Navigate to="/app/pipeline" replace />} />
        <Route path="/app/library" element={<Navigate to="/app/documents" replace />} />

        {/* App Routes - With MainLayout (protected) */}
        <Route path="/app" element={<Navigate to="/app/pipeline" replace />} />
        <Route
          path="/app/pipeline"
          element={
            <PrivateRoute>
              <MainLayout>
                <PipelinePage />
              </MainLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/app/pipeline/:jobId"
          element={
            <PrivateRoute>
              <MainLayout>
                <ErrorBoundary name="JobDetailPage">
                  <JobDetailPage />
                </ErrorBoundary>
              </MainLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/app/dashboard"
          element={
            <PrivateRoute>
              <MainLayout>
                <DashboardPage />
              </MainLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/app/analytics"
          element={
            <PrivateRoute>
              <MainLayout>
                <AnalyticsPage />
              </MainLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/app/documents"
          element={
            <PrivateRoute>
              <MainLayout>
                <DocumentsPage />
              </MainLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/app/profile"
          element={
            <PrivateRoute>
              <MainLayout>
                <ProfilePage />
              </MainLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/app/referral"
          element={
            <PrivateRoute>
              <MainLayout>
                <ReferralPage />
              </MainLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/app/onboarding"
          element={
            <PrivateRoute>
              <MainLayout showSidebar={false}>
                <OnboardingPage />
              </MainLayout>
            </PrivateRoute>
          }
        />

        {/* Catch-all - redirect to app */}
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
